import React, { useEffect, useState } from 'react';

import { useSnackbar } from 'notistack';

import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  ListItemText,
  MenuItem,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from '@mui/material';

import { ALL_METRICS_SECTION, RULE_HEADINGS } from '~/components/GoogleAds/constants';

import {
  RuleTemplate,
  createRuleTemplate,
  getDefaultRuleClassifications,
  updateRuleTemplate,
} from '~/services/PublicOfferFeedService';

type Props = {
  open: boolean;
  setOpen: (open: boolean) => void;
  fetchData: () => void;
  editableTemplate: RuleTemplate | null;
};

// Convert shared constants to the format needed for this component
const AVAILABLE_METRICS = ALL_METRICS_SECTION.map((key) => ({
  key,
  label: RULE_HEADINGS[key],
}));

const STEPS = ['Details', 'Metrics & Rules', 'Classifications'];

export default function GoogleAdsRuleTemplateForm({ open, setOpen, fetchData, editableTemplate }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeStep, setActiveStep] = useState(0);

  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedMetrics, setSelectedMetrics] = useState<Array<string>>([]);
  const [ruleDefinitions, setRuleDefinitions] = useState<Record<string, { H: number; M: number; L: number }>>({});
  const [ruleClassifications, setRuleClassifications] = useState(getDefaultRuleClassifications());

  // Form validation
  const [errors, setErrors] = useState<{
    name?: string;
    metrics?: string;
  }>({});

  const isEditMode = !!editableTemplate;

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (open) {
      if (editableTemplate) {
        // Edit mode - populate with existing data
        setName(editableTemplate.name);
        setDescription(editableTemplate.description || '');
        setSelectedMetrics(editableTemplate.primary_metrics);
        setRuleDefinitions(editableTemplate.rule_definitions as Record<string, { H: number; M: number; L: number }>);
        setRuleClassifications(editableTemplate.rule_classifications);
      } else {
        // Create mode - reset form
        setName('');
        setDescription('');
        setSelectedMetrics([]);
        setRuleDefinitions({});
        setRuleClassifications(getDefaultRuleClassifications());
      }
      setErrors({});
      setActiveStep(0);
    }
  }, [open, editableTemplate]);

  const handleDefinitionChange = (metric: string, level: 'H' | 'M' | 'L', value: string) => {
    const numValue = parseFloat(value) || 0;
    setRuleDefinitions((prev) => ({
      ...prev,
      [metric]: {
        ...prev[metric],
        [level]: numValue,
      },
    }));
  };

  const validateForm = () => {
    const newErrors: { name?: string; metrics?: string } = {};

    if (!name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (selectedMetrics.length !== 3) {
      newErrors.metrics = 'Please select exactly 3 metrics';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        name: name.trim(),
        description: description.trim() || null,
        primary_metrics: selectedMetrics,
        rule_definitions: ruleDefinitions,
        rule_classifications: ruleClassifications,
      };

      if (isEditMode && editableTemplate) {
        await updateRuleTemplate(editableTemplate.id, payload);
        enqueueSnackbar('Rule template updated successfully', { variant: 'success' });
      } else {
        await createRuleTemplate(payload);
        enqueueSnackbar('Rule template created successfully', { variant: 'success' });
      }

      setOpen(false);
      fetchData();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An error occurred';
      enqueueSnackbar(`Failed to ${isEditMode ? 'update' : 'create'} rule template: ${errorMessage}`, {
        variant: 'error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setOpen(false);
    }
  };

  const validateStep = (step: number): boolean => {
    const newErrors: { name?: string; metrics?: string } = {};

    if (step === 0) {
      if (!name.trim()) {
        newErrors.name = 'Name is required';
      }
    }

    if (step === 1) {
      if (selectedMetrics.length !== 3) {
        newErrors.metrics = 'Please select exactly 3 metrics';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleMetricChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const value = Array.isArray(e.target.value)
      ? e.target.value
      : typeof e.target.value === 'string'
      ? e.target.value.split(',')
      : [];
    if (value.length <= 3) {
      setSelectedMetrics(value);
      if (errors.metrics) {
        setErrors({ ...errors, metrics: undefined });
      }
    }
  };

  // Render step content
  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Stack spacing={2}>
            <TextField
              label="Template Name"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (errors.name) {
                  setErrors({ ...errors, name: undefined });
                }
              }}
              error={!!errors.name}
              helperText={errors.name}
              fullWidth
              required
              size="small"
            />
            <TextField
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              fullWidth
              multiline
              rows={2}
              size="small"
              placeholder="Optional"
            />
          </Stack>
        );

      case 1:
        return (
          <Stack spacing={2}>
            <TextField
              select
              label="Primary Metrics"
              value={selectedMetrics}
              onChange={handleMetricChange}
              error={!!errors.metrics}
              helperText={errors.metrics || `Select 3 metrics (${selectedMetrics.length}/3)`}
              fullWidth
              size="small"
              slotProps={{
                select: {
                  multiple: true,
                  MenuProps: {
                    PaperProps: {
                      sx: { maxHeight: 280 },
                    },
                  },
                  renderValue: (selected: unknown) =>
                    (selected as Array<string>)
                      .map((key) => AVAILABLE_METRICS.find((m) => m.key === key)?.label ?? key)
                      .join(', '),
                },
              }}
            >
              {AVAILABLE_METRICS.map((metric) => (
                <MenuItem
                  key={metric.key}
                  value={metric.key}
                  disabled={!selectedMetrics.includes(metric.key) && selectedMetrics.length >= 3}
                  dense
                >
                  <Checkbox checked={selectedMetrics.includes(metric.key)} size="small" />
                  <ListItemText primary={metric.label} />
                </MenuItem>
              ))}
            </TextField>

            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Rule Definitions
              </Typography>
              <Stack spacing={1.5} sx={{ minHeight: 210 }}>
                {[0, 1, 2].map((index) => {
                  const metricKey = selectedMetrics[index];
                  const metric = metricKey ? AVAILABLE_METRICS.find((m) => m.key === metricKey) : null;
                  const values = metricKey ? ruleDefinitions[metricKey] || { H: 0, M: 0, L: 0 } : null;

                  return (
                    <Box key={index} sx={{ opacity: metricKey ? 1 : 0.4 }}>
                      <Typography variant="caption" color="text.secondary">
                        {metric?.label || `Select metric ${index + 1}`}
                      </Typography>
                      <Stack direction="row" spacing={1}>
                        <TextField
                          label="High"
                          type="number"
                          size="small"
                          value={values?.H ?? ''}
                          onChange={(e) => metricKey && handleDefinitionChange(metricKey, 'H', e.target.value)}
                          disabled={!metricKey}
                          sx={{ flex: 1 }}
                        />
                        <TextField
                          label="Medium"
                          type="number"
                          size="small"
                          value={values?.M ?? ''}
                          onChange={(e) => metricKey && handleDefinitionChange(metricKey, 'M', e.target.value)}
                          disabled={!metricKey}
                          sx={{ flex: 1 }}
                        />
                        <TextField
                          label="Low"
                          type="number"
                          size="small"
                          value={values?.L ?? ''}
                          onChange={(e) => metricKey && handleDefinitionChange(metricKey, 'L', e.target.value)}
                          disabled={!metricKey}
                          sx={{ flex: 1 }}
                        />
                      </Stack>
                    </Box>
                  );
                })}
              </Stack>
            </Box>
          </Stack>
        );

      case 2:
        return (
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Rule Classifications
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(4, 1fr)',
                gap: 2,
              }}
            >
              {Object.entries(ruleClassifications).map(([key, value]) => (
                <TextField
                  key={key}
                  label={key}
                  type="number"
                  size="small"
                  value={value}
                  onChange={(e) => {
                    const numValue = parseInt(e.target.value) || 0;
                    setRuleClassifications((prev) => ({
                      ...prev,
                      [key]: numValue,
                    }));
                  }}
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    '& .MuiInputLabel-root': { fontSize: '0.7rem' },
                    '& .MuiInputBase-input': { fontSize: '0.85rem', py: 0.75 },
                  }}
                />
              ))}
            </Box>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>{isEditMode ? 'Edit' : 'Create'} Rule Template</DialogTitle>
      <DialogContent sx={{ pb: 1 }}>
        <Stepper activeStep={activeStep} sx={{ pt: 1, pb: 2 }}>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        {renderStepContent(activeStep)}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={isSubmitting} variant="text">
          Cancel
        </Button>
        <Box sx={{ flex: 1 }} />
        {activeStep > 0 && (
          <Button onClick={handleBack} disabled={isSubmitting} variant="text">
            Back
          </Button>
        )}
        {activeStep < STEPS.length - 1 ? (
          <Button onClick={handleNext} variant="contained" color="primary">
            Next
          </Button>
        ) : (
          <Button onClick={handleSubmit} disabled={isSubmitting} variant="contained" color="primary">
            {isSubmitting ? (isEditMode ? 'Updating...' : 'Creating...') : isEditMode ? 'Update' : 'Create'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
